// Retire le `?_gl=…` que Google colle aux liens internes ouverts en nouvel
// onglet (cartes de /recherche, événements de la page d'une asso, etc.).
//
// D'où il vient : `gtag('set', 'url_passthrough', true)` (nuxt.config). Pour
// un visiteur qui n'a pas accepté les cookies, gtag réécrit le `href` de
// chaque lien du même domaine au `mousedown` / `keyup` (vérifié dans gtag.js
// 2026-09-26 : `id(document, "mousedown", …)`). En navigation NuxtLink dans
// le même onglet, le routeur ignore ce href → rien ne se voit. En
// `target="_blank"` (ou clic molette / « ouvrir dans un nouvel onglet »),
// le navigateur ouvre le href réécrit → adresse polluée, copiée, partagée.
//
// Le nouvel onglet est VOULU (Karim 2026-09-26) : on ne touche pas à
// l'ouverture, on nettoie le href entre le `mousedown` de gtag et
// l'ouverture (`click` / `auxclick` / `contextmenu` arrivent après).
//
// Sens du risque : on ne retire le `_gl` QUE s'il ne transporte rien d'autre
// que `_up` (marqueur passthrough) et `_ga*` (identifiant GA4 sans cookie).
// Dès qu'il contient une info publicitaire (`_gcl_aw`, `_gcl_au`… = clic
// Google Ads), on le LAISSE : l'attribution des conversions passe avant
// l'esthétique de l'adresse. Seuls les liens du même domaine sont touchés —
// le linker vers dashboard.donoor.org reste intact.

/** Clés du `_gl` qui ne servent qu'à GA4, sans enjeu publicitaire. */
function estCleSansEnjeu(cle: string): boolean {
  return cle === "_up" || cle === "_ga" || cle.startsWith("_ga_");
}

/**
 * Format du `_gl` : `1*empreinte*cle*valeur*cle*valeur…` (valeurs en base64
 * sans `*`). Vrai seulement si toutes les clés sont sans enjeu.
 */
function glSansInfoPub(gl: string): boolean {
  const morceaux = gl.split("*");
  if (morceaux.length < 4) return false;
  const cles = morceaux.slice(2).filter((_, i) => i % 2 === 0);
  return cles.length > 0 && cles.every(estCleSansEnjeu);
}

function nettoyerLien(lien: HTMLAnchorElement): void {
  if (!lien.href) return;
  let url: URL;
  try {
    url = new URL(lien.href);
  } catch {
    return;
  }
  if (url.host !== window.location.host) return;
  const gl = url.searchParams.get("_gl");
  if (gl === null || !glSansInfoPub(gl)) return;
  url.searchParams.delete("_gl");
  lien.href = url.toString();
}

export default defineNuxtPlugin(() => {
  // gtag (et donc le `_gl`) n'existe qu'en prod.
  const hostname = window.location.hostname;
  if (hostname !== "donoor.org" && hostname !== "www.donoor.org") return;

  const surEvenement = (e: Event) => {
    const cible = e.target as Element | null;
    const lien = cible?.closest?.("a");
    if (lien instanceof HTMLAnchorElement) nettoyerLien(lien);
  };
  // Phase de CAPTURE sur window : passe avant tout autre écouteur de la page.
  for (const type of ["click", "auxclick", "contextmenu"]) {
    window.addEventListener(type, surEvenement, true);
  }
});
